import * as React from "react";
import { Alert, AlertColor, Grid, Link } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { Link as RouterLink } from "react-router-dom";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import AddProjectDrawer from "./addProjectDrawer";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { useDeletePayrollProjectMutation, useGetMiscProjectsQuery, useGetReferenceDataQuery } from "../../../services/cclTokenedPayrollApi";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

type PayrollProject = {
    id: string;
    projectId: string;
    eventCode: string;
    sessionTitle: string;
    sessionType: string;
    customer: string;
    adminLoc: string;
    glGroup: string;
    created: string;
    createdBy: string;
};

const ProjectsPanel: React.FC = () => {
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [createDrawerOpen, setCreateDrawerOpen] = React.useState(false);
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const { data: miscProjectsData, isLoading, isError } = useGetMiscProjectsQuery();
    const { data: referenceData } = useGetReferenceDataQuery();
    const [deletePayrollProject, { isLoading: isDeleting }] = useDeletePayrollProjectMutation();
    const { logEvent } = useLogAccessEvent();

    const projectsFromApi: PayrollProject[] = (Array.isArray(miscProjectsData)
        ? miscProjectsData
        : miscProjectsData?.data ?? []
    ).map((project: any) => {
        const projectId = String(project.projectId ?? project.id ?? "");
        return {
            ...project,
            id: String(project.id ?? projectId),
            projectId,
            eventCode: String(project.eventCode ?? ""),
            sessionTitle: String(project.sessionTitle ?? project.projectName ?? project.eventTitle ?? ""),
            sessionType: String(project.sessionType ?? project.lob ?? project.lineOfBusiness ?? ""),
            customer: String(project.customer ?? project.client ?? project.clientName ?? ""),
            adminLoc: String(project.adminLoc ?? project.managingLocation ?? ""),
            glGroup: String(project.glGroup ?? ""),
        };
    }).filter((project: PayrollProject) => project.projectId);
    const projects = projectsFromApi;

    const handleToolbarButtonClick = async (buttonId: string) => {
        if (buttonId === "create-project") {
            setCreateDrawerOpen(true);
            return;
        }

        if (buttonId === "delete-project" && selectedRows.length === 1 && !isDeleting) {
            const project = projects.find((row) => row.id === String(selectedRows[0]));
            if (!project) {
                return;
            }

            try {
                await deletePayrollProject(project.projectId).unwrap();
                logEvent("PayrollProjectDeleted", {
                    sessionID: "",
                    projectCode: project.eventCode,
                });
                setSelectedRows([]);
                showResult("Payroll Project Deleted", "success");
            } catch {
                showResult("Unable to delete payroll project.", "error");
            }
        }
    };

    const showResult = (message: string, severity: AlertColor) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setShowSnackbar(true);
    };

    const columns: GridColDef[] = [
        {
            field: "eventCode",
            headerName: "Project Id",
            width: 140,
            renderCell: (params) => (
                <Link
                    component={RouterLink}
                    to={`/payroll-project-details/${params.row.projectId}`}
                    state={{ project: params.row }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {params.value}
                </Link>
            ),
        },
        {
            field: "projectName",
            headerName: "Session Title",
            renderCell: (params) => (
                <Link
                    component={RouterLink}
                    to={`/payroll-project-details/${params.row.projectId}`}
                    state={{ project: params.row }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {params.value}
                </Link>
            ),
            width: 200,
        },
        {
            field: "lob",
            headerName: "Session Type",
            width: 200,
            valueGetter: (params) => {
                const description = referenceData?.linesOfBusiness?.find(
                    (lob: any) => String(lob.lobCode) === String(params.value)
                )?.lobDescription;
                return description ? `${params.value} - ${description}` : params.value;
            },
        },
        {
            field: "client",
            headerName: "Customer",
            width: 200,
        },
        {
            field: "adminLoc",
            headerName: "Admin. Location",
            width: 200,
            valueGetter: (params) => {
                const description = referenceData?.locations?.find(
                    (location: any) => String(location.locCode) === String(params.value)
                )?.locDesc;
                return description ? `${params.value} - ${description}` : params.value;
            },
        },
        {
            field: "glGroup",
            headerName: "GL Group",
            width: 200,
        },
        {
            field: "created",
            headerName: "Created",
            width: 200,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : "",
        },
        {
            field: "createdBy",
            headerName: "Created By",
            width: 200,
        }
    ];

    return (
        <Grid container spacing={0} alignItems="stretch" sx={{ justify: "space-between", flexGrow: 1 }}>
            <AddProjectDrawer
                open={createDrawerOpen}
                onClose={() => setCreateDrawerOpen(false)}
                onSaveResult={showResult}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                item
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    height: "calc(100vh - 220px)",
                    minHeight: 400,
                }}
            >
                {isError && <Alert severity="error">Unable to load payroll projects.</Alert>}
                <DataGridPremium
                    columns={columns}
                    rows={projects}
                    initialState={{ sorting: { sortModel: [{ field: "eventCode", sort: "desc" }] } }}
                    loading={isLoading}
                    getRowId={(row) => row.id ?? row.projectId}
                    components={{ Toolbar: CclDataGridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showSaveState: false,
                            hideSettingsButtons: true,
                            savestate: null,
                            clearstate: null,
                            miscButtons: [
                                {
                                    id: "create-project",
                                    caption: "Create Project",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                },
                                {
                                    id: "delete-project",
                                    caption: "Delete Project",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: selectedRows.length !== 1 || isDeleting,
                                },
                            ],
                            btnClick: handleToolbarButtonClick,
                        },
                    }}
                    checkboxSelection
                    onSelectionModelChange={(newSelection: any) =>
                        setSelectedRows(newSelection as Array<string | number>)
                    }
                    disableSelectionOnClick
                />
            </Grid>
        </Grid>
    );
};

export default ProjectsPanel;